import { SoundType } from '@/hooks/useAudio'

// 全局音频播放实例（无需 React Hook）
class GlobalAudioManager {
  private static instance: GlobalAudioManager
  private audioCache: { [key in SoundType]?: HTMLAudioElement } = {}
  private enabled: boolean = true
  private globalVolume: number = 0.7

  private constructor() {}

  static getInstance(): GlobalAudioManager {
    if (!GlobalAudioManager.instance) {
      GlobalAudioManager.instance = new GlobalAudioManager()
    }
    return GlobalAudioManager.instance
  }

  private getAudioPath(type: SoundType): string {
    // 使用相对于 public 目录的路径，React 打包后会正确处理
    const paths = {
      success: '/audios/ok.mp3',
      error: '/audios/error.mp3'
    }
    return paths[type]
  }

  private getAudio(type: SoundType): HTMLAudioElement {
    if (!this.audioCache[type]) {
      const audio = new Audio(this.getAudioPath(type))
      audio.preload = 'auto'
      audio.volume = this.globalVolume
      this.audioCache[type] = audio
    }
    return this.audioCache[type]!
  }

  // 播放音效
  play(type: SoundType, volume?: number): void {
    if (!this.enabled) return

    try {
      const audio = this.getAudio(type)
      audio.currentTime = 0
      
      if (volume !== undefined) {
        audio.volume = Math.max(0, Math.min(1, volume))
      }
      
      const playPromise = audio.play()
      if (playPromise) {
        playPromise.catch((error) => {
          console.warn('音频播放失败:', error)
        })
      }
    } catch (error) {
      console.warn('音频播放失败:', error)
    }
  }

  // 便捷方法
  playSuccess(volume?: number): void {
    this.play('success', volume)
  }

  playError(volume?: number): void {
    this.play('error', volume)
  }

  // 预加载所有音效
  preloadAll(): void {
    this.getAudio('success')
    this.getAudio('error')
  }

  // 启用/禁用音效
  setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }

  // 设置全局音量
  setGlobalVolume(volume: number): void {
    this.globalVolume = Math.max(0, Math.min(1, volume))
    Object.values(this.audioCache).forEach(audio => {
      if (audio) {
        audio.volume = this.globalVolume
      }
    })
  }

  // 获取状态
  isEnabled(): boolean {
    return this.enabled
  }

  getGlobalVolume(): number {
    return this.globalVolume
  }
}

// 导出全局实例
export const audioManager = GlobalAudioManager.getInstance()

// 便捷的全局函数
export const playSuccessSound = (volume?: number) => audioManager.playSuccess(volume)
export const playErrorSound = (volume?: number) => audioManager.playError(volume)
export const preloadAudios = () => audioManager.preloadAll()
export const setAudioEnabled = (enabled: boolean) => audioManager.setEnabled(enabled)
export const setAudioVolume = (volume: number) => audioManager.setGlobalVolume(volume)